/**
 * Inscrição Estadual — validação do dígito verificador.
 *
 * ESCOPO, e isto importa: só São Paulo é validado de verdade. Cada um dos 27
 * estados tem formato e algoritmo próprios, com tamanhos de 8 a 14 dígitos.
 * Escrever as 27 regras sem conferir cada uma em fonte oficial seria pior que
 * não validar: um peso errado bloqueia o cadastro de um cliente legítimo, e
 * "não consigo cadastrar hoje" é pior que "a nota vai ser rejeitada em duas
 * semanas".
 *
 * Por isso a divisão: SP bloqueia, o resto avisa.
 *
 * Contexto de por que isto existe (12/08/2026): a nota 100101 foi rejeitada com
 * cStat 210, e a varredura em produção achou 12 cadastros de SP com IE que não
 * fecha a conta — 9 deles com exatamente 10 dígitos, que é a capacidade da
 * máscara de RG do próprio campo.
 */

/** Só os dígitos. Não usar para gravar: o campo também guarda RG e "ISENTO". */
const soDigitos = (valor: string): string => String(valor ?? '').replace(/\D/g, '');

/**
 * Dígito verificador de SP: DV = (soma dos produtos % 11) % 10.
 *
 * O `% 11` no meio é essencial - sem ele a fórmula reprova IE válida. Aferido
 * contra a IE da própria PROMATEC, 582842200110: soma1 = 131 (131 % 11 = 10 -> 0,
 * igual à 9ª posição) e soma2 = 186 (186 % 11 = 10 -> 0, igual à 12ª).
 */
const dv = (digitos: string, pesos: number[]): number => {
  const soma = pesos.reduce(
    (acc, peso, i) => acc + Number(digitos[i]) * peso,
    0
  );
  return (soma % 11) % 10;
};

const PESOS_1 = [1, 3, 4, 5, 6, 7, 8, 10];
const PESOS_2 = [3, 2, 10, 9, 8, 7, 6, 5, 4, 3, 2];

/**
 * Valida IE de São Paulo, nos dois formatos que o estado tem:
 *
 *   - comum: 12 dígitos, verificador na 9ª e na 12ª posição
 *   - produtor rural: "P" + 12 dígitos, verificador SÓ na 9ª. Os três últimos
 *     dígitos são o número do estabelecimento e não têm verificador - aplicar o
 *     2º dígito aqui reprovaria IE de produtor válida.
 *
 * Devolve true quando não há o que validar (vazio, ou "ISENTO" sem dígito): quem
 * decide se o campo é obrigatório é o schema, não esta função.
 */
export const ieSpValida = (valor: string): boolean => {
  const bruto = String(valor ?? '').trim();
  const digitos = soDigitos(bruto);

  if (digitos.length === 0) return true;

  const produtorRural = /^p/i.test(bruto);

  if (produtorRural) {
    if (digitos.length !== 12) return false;
    return dv(digitos, PESOS_1) === Number(digitos[8]);
  }

  if (digitos.length !== 12) return false;

  return (
    dv(digitos, PESOS_1) === Number(digitos[8]) &&
    dv(digitos, PESOS_2) === Number(digitos[11])
  );
};

/** O campo RG/IE só carrega IE quando o documento é CNPJ. Em CPF ele guarda RG. */
export const documentoEhCnpj = (cpfCnpj: string): boolean =>
  soDigitos(cpfCnpj).length === 14;

/**
 * Mensagem para o campo RG/IE. Devolve string vazia quando não há nada a dizer.
 *
 * `bloqueia` distingue os dois comportamentos: em SP o schema usa isto para
 * reprovar o Salvar; nos outros estados a mesma mensagem vai como texto de apoio
 * do campo, sem impedir de gravar.
 */
export const avaliarIe = (
  valor: string,
  uf: string,
  cpfCnpj: string
): { mensagem: string; bloqueia: boolean } => {
  const digitos = soDigitos(valor);

  // Sem dígito nenhum não há o que conferir. Inclui o caso de quem escreve
  // "ISENTO", que é resposta legítima: a FN_CLIENTE_IE trata isso como sem IE e
  // a nota sai com indIEDest 2/9.
  if (digitos.length === 0) return { mensagem: '', bloqueia: false };

  // Documento de pessoa física: o campo está guardando RG, não IE.
  if (!documentoEhCnpj(cpfCnpj)) return { mensagem: '', bloqueia: false };

  const estado = String(uf ?? '').trim().toUpperCase();

  if (estado === 'SP') {
    if (ieSpValida(valor)) return { mensagem: '', bloqueia: false };
    return {
      mensagem:
        'Inscrição Estadual inválida para SP (o dígito verificador não fecha). ' +
        'Confira por CNPJ no CADESP - a nota fiscal será rejeitada com esta IE.',
      bloqueia: true,
    };
  }

  // Fora de SP: só o que é seguro afirmar sem a regra do estado na mão. Nenhuma
  // IE do país tem menos de 8 dígitos, e 10 é o tamanho da máscara de RG deste
  // campo - foi assim que 9 cadastros de SP ficaram com IE de 10 dígitos.
  if (!estado) return { mensagem: '', bloqueia: false };

  if (digitos.length < 8) {
    return {
      mensagem: `Esta IE tem ${digitos.length} dígitos, e nenhum estado usa menos de 8. Confira no Sintegra de ${estado}.`,
      bloqueia: false,
    };
  }

  return { mensagem: '', bloqueia: false };
};
